<template>
  <div class="ui-toast" :class="`_${type}`" role="alert" aria-live="assertive">
    <div class="ui-toast__icon">
      <IconCircleCheck v-if="type === 'success'" />
      <IconAlertCircle v-else-if="type === 'error'" />
      <IconAlertTriangle v-else-if="type === 'warning'" />
      <IconInfo v-else />
    </div>
    <div class="ui-toast__message">{{ message }}</div>
    <UiButton
      v-if="closable"
      icon
      small
      class="ui-toast__close"
      aria-label="Close"
      @click="emit('close')"
    >
      <IconX />
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface UiToastProps {
  message: string
  type?: ToastType
  closable?: boolean
  duration?: number | null
}

const props = withDefaults(defineProps<UiToastProps>(), {
  type: 'info',
})

const emit = defineEmits<{
  close: []
}>()

let timer: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  if (props.duration != null && props.duration > 0) {
    timer = setTimeout(() => emit('close'), props.duration)
  }
})

onBeforeUnmount(() => {
  if (timer != null) {
    clearTimeout(timer)
  }
})
</script>

<style lang="sass" scoped>
@use '@/styles/variables'
@use '@/styles/mixins'
@use 'sass:map'

.ui-toast
  --ui-toast-bg: #BDE5FF
  --ui-toast-border:  #{map.get(variables.$border-color, 'info')}
  --ui-toast-icon-color: #{map.get(variables.$theme-color, 'info')}

  display: flex
  align-items: center
  gap: variables.$spacer * 0.5
  min-height: 44px
  min-width: 225px
  padding: variables.$spacer * 0.5 variables.$spacer
  border-radius: map.get(variables.$border-radius, 'small')
  border: 1px solid var(--ui-toast-border)
  background-color: var(--ui-toast-bg)

  &._success
    --ui-toast-bg: #B6E8C3
    --ui-toast-border: #{map.get(variables.$border-color, 'success')}
    --ui-toast-icon-color: #{map.get(variables.$theme-color, 'success')}

  &._error
    --ui-toast-bg: #FFC4C8
    --ui-toast-border: #{map.get(variables.$border-color, 'error')}
    --ui-toast-icon-color: #{map.get(variables.$theme-color, 'error')}

  &._warning
    --ui-toast-bg: #FFE4CB
    --ui-toast-border: #{map.get(variables.$border-color, 'warning')}
    --ui-toast-icon-color: #{map.get(variables.$theme-color, 'warning')}

  &__icon
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    width: 24px
    height: 24px
    color: var(--ui-toast-icon-color)

    svg
      color: currentColor

  &__message
    flex: 1 0 0
    color: map.get(variables.$text-color, 'primary')
    min-width: 0
    cursor: default
    word-break: break-word
    +mixins.typography(map.get(variables.$input-typography, 'default'))
</style>
